import { useEffect, useState } from 'react';

export const usePWAInstall = () => {
  // Initialize from the globally-captured event (set in index.js before React
  // mounts) so the button appears even when the browser fires the event before
  // useEffect can attach its own listener.
  const [promptEvent, setPromptEvent] = useState(() => window.__pwaInstallPrompt || null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Already running in standalone (installed)
    const mq = window.matchMedia('(display-mode: standalone)');
    if (mq.matches || window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Pick up the global in case it was set between render and this effect
    if (window.__pwaInstallPrompt && !promptEvent) {
      setPromptEvent(window.__pwaInstallPrompt);
    }

    const onPrompt = (e) => {
      e.preventDefault();          // suppress mini-infobar; we show our own button
      window.__pwaInstallPrompt = e;
      setPromptEvent(e);
    };

    const onInstalled = () => {
      window.__pwaInstallPrompt = null;
      setPromptEvent(null);
      setIsInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Session 154 — Force a fresh install pass.
  //
  // When the PWA is already installed (or the browser has stopped firing
  // `beforeinstallprompt` because it already did), there's no way to
  // programmatically re-show Chrome's native install dialog. The next-best
  // thing — and what the user actually wants ("re-install ... to update the
  // icon and other installation") — is to wipe the service-worker cache so
  // the browser re-fetches the manifest + icons on next load. Chrome's
  // install heuristic then re-runs and may resurface the install prompt.
  //
  // Steps:
  //   1. Unregister every active service worker.
  //   2. Delete every cache via the Cache Storage API.
  //   3. Reload (no cache) so the fresh manifest/icons land immediately.
  const reinstall = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (typeof caches !== 'undefined' && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } finally {
      // Force reload from network so the freshly cleared SW + new
      // manifest/icons are picked up on the very next paint.
      window.location.reload();
    }
  };

  // Install handler used by the login-page CTA.
  //   • Native prompt available  → fire it (Chrome / Edge / Samsung).
  //   • Already installed or no prompt available → return a sentinel
  //     so the caller can decide whether to fall back to `reinstall()`
  //     (with a confirmation toast / dialog).
  //
  // Return contract:
  //   { outcome: 'accepted' | 'dismissed' | 'unavailable' }
  const install = async () => {
    if (!promptEvent) return { outcome: 'unavailable' };
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === 'accepted') {
      window.__pwaInstallPrompt = null;
      setPromptEvent(null);
      setIsInstalled(true);
    }
    return { outcome };
  };

  return {
    canInstall: !!promptEvent && !isInstalled,
    isInstalled,
    install,
    reinstall,
  };
};
